import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import type { FAQ } from '@/types/home'

const GRID_ACCENTS = [
  {
    card: 'border-primary-200/80 bg-linear-to-br from-primary-50 via-white to-primary-50/30',
    open: 'border-primary-300 shadow-[0_8px_24px_rgba(15,93,154,0.12)]',
    btn: 'bg-primary-500 text-white shadow-sm shadow-primary-500/25',
    q: 'text-primary-900 group-hover:text-primary-700',
    openQ: 'text-primary-800',
    answer: 'border-primary-100/80',
  },
  {
    card: 'border-sky-200/80 bg-linear-to-br from-sky-50 via-white to-cyan-50/40',
    open: 'border-sky-300 shadow-[0_8px_24px_rgba(14,165,233,0.12)]',
    btn: 'bg-sky-500 text-white shadow-sm shadow-sky-500/25',
    q: 'text-sky-950 group-hover:text-sky-700',
    openQ: 'text-sky-800',
    answer: 'border-sky-100/80',
  },
  {
    card: 'border-emerald-200/80 bg-linear-to-br from-emerald-50 via-white to-teal-50/40',
    open: 'border-emerald-300 shadow-[0_8px_24px_rgba(16,185,129,0.12)]',
    btn: 'bg-emerald-500 text-white shadow-sm shadow-emerald-500/25',
    q: 'text-emerald-950 group-hover:text-emerald-700',
    openQ: 'text-emerald-800',
    answer: 'border-emerald-100/80',
  },
  {
    card: 'border-violet-200/80 bg-linear-to-br from-violet-50 via-white to-purple-50/40',
    open: 'border-violet-300 shadow-[0_8px_24px_rgba(139,92,246,0.12)]',
    btn: 'bg-violet-500 text-white shadow-sm shadow-violet-500/25',
    q: 'text-violet-950 group-hover:text-violet-700',
    openQ: 'text-violet-800',
    answer: 'border-violet-100/80',
  },
  {
    card: 'border-amber-200/80 bg-linear-to-br from-amber-50 via-white to-orange-50/40',
    open: 'border-amber-300 shadow-[0_8px_24px_rgba(245,158,11,0.12)]',
    btn: 'bg-amber-500 text-white shadow-sm shadow-amber-500/25',
    q: 'text-amber-950 group-hover:text-amber-700',
    openQ: 'text-amber-900',
    answer: 'border-amber-100/80',
  },
] as const

export function FAQAccordion({
  faqs,
  defaultOpenIndex = 0,
  collapseAllOnMobile = false,
  className,
  variant = 'default',
  columns = 1,
}: {
  faqs: FAQ[]
  defaultOpenIndex?: number | null
  /** When true, no items are expanded by default below the `md` breakpoint */
  collapseAllOnMobile?: boolean
  className?: string
  variant?: 'default' | 'premium' | 'color-grid'
  columns?: 1 | 2
}) {
  const isColorGrid = variant === 'color-grid'
  const isPremium = variant === 'premium'

  return (
    <div
      className={cn(
        isColorGrid && columns === 2 && 'grid grid-cols-1 gap-4 md:grid-cols-2',
        isPremium && 'space-y-2.5',
        variant === 'default' && 'divide-y divide-neutral-100',
        className,
      )}
    >
      {faqs.map((faq, index) => (
        <FAQAccordionItem
          key={faq.id}
          faq={faq}
          defaultOpen={defaultOpenIndex != null && index === defaultOpenIndex}
          collapseOnMobile={collapseAllOnMobile}
          variant={variant}
          accent={isColorGrid ? GRID_ACCENTS[index % GRID_ACCENTS.length] : undefined}
          spanFull={isColorGrid && columns === 2 && faqs.length % 2 !== 0 && index === faqs.length - 1}
        />
      ))}
    </div>
  )
}

function FAQAccordionItem({
  faq,
  defaultOpen,
  collapseOnMobile,
  variant,
  accent,
  spanFull,
}: {
  faq: FAQ
  defaultOpen?: boolean
  collapseOnMobile?: boolean
  variant: 'default' | 'premium' | 'color-grid'
  accent?: (typeof GRID_ACCENTS)[number]
  spanFull?: boolean
}) {
  const [open, setOpen] = useState(() => {
    const shouldOpen = defaultOpen ?? false
    if (
      collapseOnMobile &&
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 767px)').matches
    ) {
      return false
    }
    return shouldOpen
  })
  const isPremium = variant === 'premium'
  const isColorGrid = variant === 'color-grid'

  return (
    <div
      className={cn(
        isPremium && 'rounded-xl border border-transparent transition-colors duration-200',
        isPremium && open && 'border-primary-100 bg-primary-50/40',
        isColorGrid &&
          accent &&
          cn(
            'h-fit overflow-hidden rounded-xl border transition-all duration-200',
            accent.card,
            open ? accent.open : 'hover:-translate-y-0.5 hover:shadow-md',
            spanFull && 'md:col-span-2',
          ),
      )}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        {...(open ? { 'aria-expanded': 'true' as const } : { 'aria-expanded': 'false' as const })}
        className={cn(
          'group flex w-full items-start justify-between gap-4 text-left transition-colors',
          isColorGrid && 'px-4 py-3 sm:px-5 sm:py-3.5',
          isPremium &&
            'rounded-xl px-4 py-3.5 hover:bg-neutral-50/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/25 sm:px-5 sm:py-4',
          !isPremium && !isColorGrid && 'py-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/25',
          isColorGrid && 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/25',
          open && !isPremium && !isColorGrid && 'text-primary-700',
        )}
      >
        <span
          className={cn(
            'font-semibold leading-snug transition-colors',
            isColorGrid && accent && cn('text-sm sm:text-[15px]', open ? accent.openQ : accent.q),
            !isColorGrid && 'text-neutral-900 group-hover:text-primary-700',
            isPremium && !isColorGrid && 'text-[15px] sm:text-base',
            !isPremium && !isColorGrid && 'text-sm',
            open && isPremium && 'text-primary-800',
          )}
        >
          {faq.question}
        </span>
        <span
          className={cn(
            'mt-0.5 flex shrink-0 items-center justify-center rounded-full transition-all duration-200',
            isColorGrid && accent && cn('h-7 w-7', open ? accent.btn : 'bg-white/80 text-neutral-500 shadow-sm'),
            isPremium &&
              'h-8 w-8 bg-neutral-100 text-neutral-500 group-hover:bg-primary-100 group-hover:text-primary-600',
            !isPremium && !isColorGrid && 'h-6 w-6',
            open && isPremium && 'bg-primary-100 text-primary-600',
          )}
        >
          <ChevronDown
            className={cn(
              'h-4 w-4 transition-transform duration-200',
              !isPremium && !isColorGrid && 'text-neutral-400',
              open && 'rotate-180',
              open && isPremium && 'text-primary-600',
              isColorGrid && open && 'text-white',
            )}
          />
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            className="overflow-hidden"
          >
            <p
              className={cn(
                'leading-relaxed text-neutral-600',
                isColorGrid &&
                  accent &&
                  cn('border-t px-4 pb-4 pt-3 text-sm sm:px-5 sm:pb-4 sm:pt-3.5', accent.answer),
                isPremium &&
                  !isColorGrid &&
                  'border-t border-primary-100/80 px-4 pb-4 pt-3 text-sm sm:px-5 sm:pb-4 sm:pt-3.5 sm:text-[15px]',
                !isPremium && !isColorGrid && 'pb-4 text-sm text-neutral-500',
              )}
            >
              {faq.answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
